<template>
  <div class="absence-sidebar text-dark">
    <div class="absence-sidebar__header">
      <div class="absence-sidebar__header__close icon__bg-circle" @click="$emit('close')">
        <span class="icon-cross2"></span>
      </div>
      <h2>{{ i18n['absent_on_date'] }}</h2>
    </div>

    <div class="absence-sidebar__date-nav">
      <button class="date-buttons" @click="prevDay">
        <span class="icon-arrow-left4"></span>
      </button>
      <DateSelector
        :date="selectedDate"
        :width100="true"
        @changed="setDate">
      </DateSelector>
      <button class="date-buttons" @click="nextDay">
        <span class="icon-arrow-right4"></span>
      </button>
    </div>

    <div class="absence-sidebar__body">
      <div class="absence-sidebar__spinner" v-if="isLoading">
        <div class="do-spinner"></div>
      </div>
      <h4 class="text-explainer" v-else-if="absentUsers.length < 1">{{ i18n['no_one_absent'] }}</h4>
      <ul v-else>
        <li class="absent-user shadow"
            v-for="user in absentUsers"
            :key="user.id"
            :class="{'absent-user--requester': user.isRequester}">
          <div class="absent-user__name">{{ user.name }}</div>
          <div class="absent-user__absence" v-for="(absence, idx) in user.absence" :key="idx">
            <b>{{ absence.title }}</b>
            <span><span class="icon-clock"></span> {{ absence.timeRange }}</span>
            <span v-if="absence.dateRange"><span class="icon-calendar4"></span> {{ absence.dateRange }}</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import docendo from '../../docendo'
import DateSelector from '../datepicker/DateSelector.vue'
import moment from 'moment'
import { mapGetters } from 'vuex'

const I18N = {
  da: {
    absent_on_date: 'Fraværende',
    no_one_absent: 'Ingen fraværende på denne dato'
  },
  en: {
    absent_on_date: 'Absent',
    no_one_absent: 'No one absent on this date'
  },
  nb: {
    absent_on_date: 'Fraværende',
    no_one_absent: 'Ingen fraværende på denne datoen'
  }
}

export default {
  components: { DateSelector },
  props: {
    date: {
      default: () => moment()
    },
    requesterId: {
      default: null
    }
  },
  data () {
    return {
      selectedDate: moment(this.date).format('YYYY-MM-DD'),
      absentToday: [],
      isLoading: false,
      requestCounter: 0
    }
  },
  computed: {
    ...mapGetters([
      'schoolId',
      'locale'
    ]),
    i18n () {
      return I18N[this.locale] || I18N['da']
    },
    absentUsers () {
      const users = this.absentToday.map(user => {
        const absence = (user.events || [])
          .filter(event => event.event_type === 'absence')
          .sort((a, b) => moment(a.start).unix() - moment(b.start).unix())
          .map(event => ({
            title: event.title,
            dateRange: this.isSameDay(event.start, event.end) ? null : this.formatDate(event.start) + ' - ' + this.formatDate(event.end),
            timeRange: this.formatTime(event.start) + ' - ' + this.formatTime(event.end)
          }))
        return {
          id: user.calendar_id,
          name: user.name,
          isRequester: !!this.requesterId && String(user.user_id) === String(this.requesterId),
          absence
        }
      })
      return this.$helpers.sortArrOnName(users)
    }
  },
  watch: {
    date (newDate, oldDate) {
      if (moment(newDate).format('YYYY-MM-DD') !== moment(oldDate).format('YYYY-MM-DD')) {
        this.setDate(newDate)
      }
    }
  },
  methods: {
    setDate (date) {
      this.selectedDate = moment(date).format('YYYY-MM-DD')
      this.getAbsence()
    },
    prevDay () {
      this.setDate(moment(this.selectedDate).subtract(1, 'days'))
    },
    nextDay () {
      this.setDate(moment(this.selectedDate).add(1, 'days'))
    },
    formatDate (date) {
      return moment(date).locale(this.locale).format('D. MMM')
    },
    isSameDay (start, end) {
      return moment(start).format('YYYY-MM-DD') === moment(end).format('YYYY-MM-DD')
    },
    formatTime (date) {
      return moment(date).locale(this.locale).format('LT')
    },
    getAbsence () {
      const counter = ++this.requestCounter
      this.isLoading = true
      docendo.absenceForDate(this.schoolId, this.selectedDate, success => {
        if (counter !== this.requestCounter) return
        this.isLoading = false
        this.absentToday = typeof success === 'string' && success.length > 1 ? JSON.parse(success) : (Array.isArray(success) ? success : [])
      }, failure => {
        if (counter !== this.requestCounter) return
        this.isLoading = false
        this.absentToday = []
      })
    }
  },
  mounted () {
    this.getAbsence()
  }
}
</script>

<style scoped>
.absence-sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 10000;
  width: 350px;
  font-size: 12px;
  overflow-y: auto;
  background: #edf0f5;
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.2);
}

@media (max-width: 1100px) {
  .absence-sidebar {
    width: 300px;
  }
}

@media (max-width: 760px) {
  .absence-sidebar {
    width: 100%;
  }
}

.absence-sidebar__header {
  position: relative;
  color: #3d4340;
  border-bottom: 1px solid #d8d8d8;
  text-align: center;
  padding: 12px;
}

.absence-sidebar__header > h2 {
  color: rgba(44, 62, 80, .8);
  text-transform: uppercase;
  font-size: 1.2rem;
}

.absence-sidebar__header__close {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  font-size: 1.2rem;
}

.absence-sidebar__date-nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 1rem;
}

.absence-sidebar__date-nav .date-buttons {
  flex-shrink: 0;
  height: 38px;
}

.absence-sidebar__spinner {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.absence-sidebar__body .text-explainer {
  margin: 0 2rem;
}

.absent-user {
  margin: 0 1rem 0.5rem;
  border-radius: 4px;
  background: #fff;
  padding: 0.5rem 0.75rem;
}

.absent-user--requester {
  box-shadow: 0 0 0 2px #4a90e2;
}

.absent-user__name {
  font-weight: bold;
  font-size: 0.875rem;
}

.absent-user__absence {
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.75rem;
  color: #475569;
}

.absent-user__absence span {
  white-space: nowrap;
}
</style>

<!-- Unscoped: vue-loader 9 understøtter ikke >>>, og reglen skal ramme DateSelectors input. -->
<style>
.absence-sidebar__date-nav .datepicker-input {
  min-width: 0 !important;
}
</style>



// WEBPACK FOOTER //
// UserRequestAbsenceSidebar.vue?519318c4